import { prettyDate, type TatPlan } from './TatPlanNotice';

/**
 * Budget vs actual for one indent — where the 9 days went, stage by stage.
 *
 * The floor (TatPlanNotice) says what we promised. This says what happened:
 * how long each hand-off actually took against its budget, which stage the
 * indent is sitting in now, and how many days are left before the promise
 * breaks. Everything is computed server-side from statusHistory
 * (services/procurement/procurement-tat-progress.ts).
 */

export type TatStageProgress = {
  stage: string;
  what: string;
  designation: string;
  person: string;
  note?: string;
  budgetDays: number;
  startedAt: string | null;
  endedAt: string | null;
  actualDays: number | null;
  varianceDays: number | null;
  state: 'done' | 'current' | 'pending';
  statusesSeen: string[];
};

export type TatProgress = {
  indentId: string;
  indentType: string;
  plan: TatPlan | null;
  startedAt: string | null;
  dueDate: string | null;
  promisedDate: string;
  elapsedDays: number | null;
  remainingDays: number | null;
  completedAt: string | null;
  actualTotalDays: number | null;
  state: 'no_plan' | 'on_track' | 'at_risk' | 'overdue' | 'completed_on_time' | 'completed_late';
  currentStage: string | null;
  currentStatus: string;
  stages: TatStageProgress[];
  dataIssues: string[];
};

const STATE_LABEL: Record<TatProgress['state'], string> = {
  no_plan: 'No TAT schedule',
  on_track: 'On track',
  at_risk: 'At risk',
  overdue: 'Overdue',
  completed_on_time: 'Delivered on time',
  completed_late: 'Delivered late',
};

const STATE_TONE: Record<TatProgress['state'], string> = {
  no_plan: 'bg-slate-100 text-slate-600 border-slate-200',
  on_track: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  at_risk: 'bg-amber-50 text-amber-900 border-amber-200',
  overdue: 'bg-rose-50 text-rose-800 border-rose-200',
  completed_on_time: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  completed_late: 'bg-rose-50 text-rose-800 border-rose-200',
};

/** Over budget is the only thing worth colouring — under budget is just good. */
function varianceTone(v: number | null): string {
  if (v === null) return 'text-slate-300';
  if (v > 0) return 'text-rose-700 font-semibold';
  if (v < 0) return 'text-emerald-700';
  return 'text-slate-500';
}

function Stat({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: string; tone?: string }) {
  return (
    <div className={'rounded-lg border px-3 py-2 ' + (tone || 'border-slate-200 bg-white')}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-lg font-bold leading-tight tabular-nums">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

export default function TatProgressPanel({ p }: { p: TatProgress }) {
  // Import has no schedule yet, so there is nothing to measure against.
  if (!p.plan) return null;

  const done = p.completedAt !== null;
  const spent = p.elapsedDays ?? 0;
  const budget = p.plan.minDays;
  // Bar can exceed 100% — that overflow IS the story on a 60-day-old indent.
  const pct = Math.min(100, Math.round((spent / Math.max(budget, 1)) * 100));
  const over = spent > budget;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat
          label="Days used"
          value={<>{spent}<span className="text-sm font-normal text-slate-400"> / {budget}</span></>}
          sub={done ? `Completed ${prettyDate(p.completedAt!)}` : 'Since the indent was raised'}
          tone={over ? 'border-rose-200 bg-rose-50' : undefined}
        />
        <Stat
          label={done ? 'Result' : 'Days remaining'}
          value={
            done
              ? <span className="text-base">{p.state === 'completed_late' ? 'Late' : 'On time'}</span>
              : <span className={p.remainingDays !== null && p.remainingDays < 0 ? 'text-rose-700' : ''}>
                  {p.remainingDays === null ? '—' : p.remainingDays < 0 ? `${Math.abs(p.remainingDays)} over` : p.remainingDays}
                </span>
          }
          sub={p.dueDate ? `TAT due ${prettyDate(p.dueDate)}` : undefined}
          tone={!done && p.remainingDays !== null && p.remainingDays < 0 ? 'border-rose-200 bg-rose-50' : undefined}
        />
        <Stat
          label="Currently at"
          value={<span className="text-base">{p.currentStage || (done ? 'Complete' : '—')}</span>}
          sub={p.currentStatus || undefined}
        />
        <Stat
          label="Promised to client"
          value={<span className="text-base">{p.promisedDate ? prettyDate(p.promisedDate) : 'Not set'}</span>}
          sub={
            p.promisedDate && p.dueDate
              ? (p.promisedDate < p.dueDate ? 'Earlier than the TAT allows' : 'Consistent with the TAT')
              : undefined
          }
          tone={p.promisedDate && p.dueDate && p.promisedDate < p.dueDate ? 'border-amber-200 bg-amber-50' : undefined}
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
          <div
            className={'h-full rounded-full ' + (over ? 'bg-rose-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500')}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        </div>
        <span className={'shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ' + STATE_TONE[p.state]}>
          {STATE_LABEL[p.state]}
        </span>
      </div>

      {p.dataIssues.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900">
          <span className="font-semibold">Audit trail problem — </span>
          {p.dataIssues.join('; ')}. Stage timings below may be understated.
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[36rem] text-left text-[12px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1.5 font-semibold">Stage</th>
              <th className="px-2 py-1.5 font-semibold">What happens</th>
              <th className="px-2 py-1.5 font-semibold">Owner</th>
              <th className="px-2 py-1.5 text-right font-semibold">Budget</th>
              <th className="px-2 py-1.5 text-right font-semibold">Actual</th>
              <th className="px-2 py-1.5 text-right font-semibold">Variance</th>
            </tr>
          </thead>
          <tbody>
            {p.stages.map((s) => (
              <tr
                key={s.stage}
                className={
                  'border-t border-slate-100 ' +
                  (s.state === 'current' ? 'bg-blue-50/60' : s.state === 'pending' ? 'text-slate-400' : '')
                }
              >
                <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[10px]">
                  {s.stage}
                  {s.state === 'current' && (
                    <span className="ml-1 rounded bg-blue-600 px-1 py-0.5 text-[9px] font-bold text-white">NOW</span>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  {s.what}
                  {s.startedAt && (
                    <span className="ml-1 text-[10px] text-slate-400">
                      {prettyDate(s.startedAt)}{s.endedAt ? ` → ${prettyDate(s.endedAt)}` : ' → now'}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5">{s.person}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{s.budgetDays > 0 ? `${s.budgetDays}d` : '—'}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {s.actualDays === null ? <span className="text-slate-300">—</span> : `${s.actualDays}d`}
                </td>
                <td className={'px-2 py-1.5 text-right tabular-nums ' + varianceTone(s.varianceDays)}>
                  {s.varianceDays === null ? '—' : s.varianceDays > 0 ? `+${s.varianceDays}d` : `${s.varianceDays}d`}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
              <td className="px-2 py-1.5" colSpan={3}>Total</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{budget}d</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{spent}d</td>
              <td className={'px-2 py-1.5 text-right tabular-nums ' + varianceTone(spent - budget)}>
                {spent - budget > 0 ? `+${spent - budget}d` : `${spent - budget}d`}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
